import { ref, watch, computed } from '@vue/composition-api';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import DirectorInterest from '@/models/DirectorInterest';
import { TABLE } from '@/constants/config';
import { pluck, formatDate, generateRandomUniqueId, formatNumber, formatPrice } from '@/helpers';
import router from '@/router';

export default function useDirectorInterestsTable() {
    const toast = useToast();
    const localUserId = ref(0);
    const refDirectorInterestViewDetailsTable = ref(null);
    const directorInterestViewList = ref(null);

    const tableColumns = [
        { key: 'index', label: '#', thClass: 'px-1 text-nowrap', tdClass: 'px-1 text-nowrap' },
        { key: 'date', label: 'DATE', tdClass: 'text-nowrap' },
        { key: 'holderName', label: 'HOLDER NAME', thClass: 'text-nowrap', tdClass: 'text-nowrap' },
        { key: 'companyName', label: 'COMPANY NAME', thClass: 'text-nowrap', tdClass: 'text-nowrap' },
        { key: 'howHeld', label: 'HOW HELD', thClass: 'text-nowrap', tdClass: 'text-nowrap' },
        { key: 'numberOfShares', label: 'NO. OF SHARES', thClass: 'text-nowrap', tdClass: 'text-nowrap' },
        // { key: 'type', label: 'TYPE', thClass: 'text-nowrap', tdClass: 'text-nowrap', formatter: value => capitalize(value) },
        { key: 'agreementDate', label: 'DATE OF AGREEMENT', thClass: 'text-right text-nowrap', tdClass: 'text-right' },
        { key: 'price', label: 'PRICE', thClass: 'text-right text-nowrap', tdClass: 'text-right text-nowrap' },
        { key: 'debit', label: 'DEBIT (ACQUIRED)', thClass: 'text-right text-nowrap', tdClass: 'text-right' },
        { key: 'credit', label: 'CREDIT (CESSATION)', thClass: 'text-right text-nowrap', tdClass: 'text-right' },
        { key: 'balance', label: 'BALANCE', thClass: ' text-right', tdClass: 'text-right' },
        { key: 'actions', label: 'ACTION', thClass: 'pl-0 pr-1 text-right', tdClass: 'px-0 pr-1 text-right' },
    ];

    const perPage = ref(TABLE.PER_PAGE);
    const totalDirectorInterests = ref(0);
    const currentPage = ref(1);
    const perPageOptions = TABLE.PER_PAGE_OPTIONS;
    const searchQuery = ref('');
    const sortBy = ref('index');
    const isSortDirDesc = ref(true);
    const isBusy = ref(false);

    const getPrice = data => {
        let price;
        let currencyCode;

        if(data.cessation_price) {
            price = data.cessation_price;
            currencyCode = data?.cessation_price_currency?.currency_code ?? data.share_capital.currency.currency_code;
        
        } else {
            price = data.acquired_price;
            currencyCode = data?.acquired_price_currency?.currency_code ?? data.share_capital.currency.currency_code;
        }

        return formatPrice(price, currencyCode);
    };

    const localizeDirectorInterestDetails = data => ({
        _id: generateRandomUniqueId(),
        id: data.id,
        userId: data.user_id,
        director: data.user ? {
            userId: data.user_id,
            id: data.user_id,
            name: data.user?.user_info?.legal_name ?? data.user.name,
            nric: data.user?.user_info?.NRIC,
            appointment: data.user.all_directors[0]?.appointment_date,
            cessation: data.user.all_directors[0]?.cessation_date,
        } : null,
        directorStatus: data.status,
        date: formatDate(data.date),
        numberOfShares: formatNumber(data.number_of_shares),
        typeOfShares: data.share_capital.share_type ? {
            id: data.share_capital.share_type.id,
            name: data.share_capital.share_type.name
        } : null,
        currency: data.share_capital.currency ? {
            id: data.share_capital.currency.id,
            name: data.share_capital.currency.name,
        } : null,
        howHeld: data.how_held,
        type: data?.type,
        companyName: data?.company_name,
        holderName: data?.holder_name,
        nameOfCompany: data.name_of_company,
        dateOfAgreement: formatDate(data.acquired_date),
        priceOrOtherConsideration: data.acquired_price ? data.acquired_price : null,        
        cessationDateOfAgreement: formatDate(data.cessation_date),
        cessationPriceOrOtherConsideration: data.cessation_price ? data.cessation_price : null,
        acquiredPriceCurrency: data.acquired_price ? data?.acquired_price_currency : null,
        cessationPriceCurrency: data.cessation_price ? data?.cessation_price_currency : null,
        agreementDate: data.acquired_date ? formatDate(data.acquired_date) : formatDate(data.cessation_date),
        price: getPrice(data),
        debit: formatNumber(data.debit),
        credit: formatNumber(data.credit),
        balance: formatNumber(data.balance),
        isGenerated: parseInt(data.is_generated, 10),
    });

    const isLoading = ref(false);

    // options where the search query is applied
    // make sure to list as keys of how to access those values in table 
    const filterOn = ref([
        'date', 
        'director.nric',
        'director.name',
    ]);

    const fetchDirectorInterests = userId => {
        localUserId.value = userId;
        isLoading.value = true;
        DirectorInterest.getDetails(userId).then(data => {
            directorInterestViewList.value = data;

            if(!data.length) {
                router.push({path: 'director-interests'});
            }

            isLoading.value = false;
        })
            .catch(e => {
                console.error("Error fetching director interest details", e);

                toast({
                    component: ToastificationContent,
                    props: {
                        title: 'Error fetching director interest details list',
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                    },
                });
            });
    };

    const dataMeta = computed(() => {
        const localItemsCount = refDirectorInterestViewDetailsTable.value ? refDirectorInterestViewDetailsTable.value.localItems.length : 0;
        totalDirectorInterests.value = localItemsCount;
        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: totalDirectorInterests.value,
        };
    });

    const refetchViewData = () => {
        refDirectorInterestViewDetailsTable.value.refresh();
    };

    watch([currentPage, perPage, searchQuery], () => {
        refetchViewData();

        const queryLowered = searchQuery.value.toLowerCase().trim();

        //  if a query present then search
        if (queryLowered)
        {
            refDirectorInterestViewDetailsTable.value.localItems = refDirectorInterestViewDetailsTable.value.localItems.filter(item => filterOn.value.some(key => {
                const value = pluck(key, item);
                return value && value.toString().toLowerCase().trim().includes(queryLowered);
            }));
        }
        isBusy.value = false;
    });

    return {
        sortBy,
        isBusy,
        perPage,
        dataMeta,
        isLoading,
        searchQuery,
        currentPage,
        tableColumns,
        isSortDirDesc,
        totalDirectorInterests,
        perPageOptions,
        refDirectorInterestViewDetailsTable,
        localizeDirectorInterestDetails,

        directorInterestViewList,
        refetchViewData,
        fetchDirectorInterests,
        filterOn,
    };
}
